import { useMachine } from '@xstate/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import throttle from 'lodash/throttle';
import isEqual from 'lodash/isEqual';
import { noOpFn } from '~/utilities/util';
import { DEFAULT_ASPECT_RATIO } from '~/singlePageApp/constants';
import type { FullyGuardedPlayerPositionManagerProps } from './FullyGuardedPlayerPositionManager';
import type { PlayerPositionManagerMachineContext } from './playerPositionManagerMachine';
import { playerPositionManagerMachine } from './playerPositionManagerMachine';
import {
  DEFAULT_BOTTOM_PX_WHEN_STICKY_AT_BOTTOM_RIGHT,
  DEFAULT_RIGHT_PX_WHEN_STICKY_AT_BOTTOM_RIGHT,
  DEFAULT_WIDTH_PX_WHEN_STICKY_AT_BOTTOM_RIGHT,
  MINIMUM_WIDTH_PX_WHEN_STICKY_AT_BOTTOM_RIGHT,
  NARROW_WINDOW_WIDTH_PX,
  REASONABLY_HORIZONTAL_ASPECT_RATIO,
} from './consts';
import {
  MediaStoreSelectors,
  useMediaStore,
} from '../../../singlePageApp/screens/Media/Store/MediaStore';

const windowIsNarrow = () => window.innerWidth <= NARROW_WINDOW_WIDTH_PX;
const HALF_SECOND = 500;

type ActionArgs = { context: PlayerPositionManagerMachineContext };

export type FullyGuardedPlayerPositionManagerArgs<TPlayerParentContainer> =
  FullyGuardedPlayerPositionManagerProps<TPlayerParentContainer>;

export const useFullyGuardedPlayerPositionManager = <TPlayerParentContainer extends HTMLElement>({
  bottomStyleWhenStickyAtBottomRight,
  intersectionObserverTarget,
  leftStyleWhenStickyAtTop,
  nonStickyPlayerPhantom,
  player,
  playerParentContainer,
  rightStyleWhenStickyAtBottomRight,
  rightStyleWhenStickyAtTop,
  topStyleWhenStickyAtTop,
  widthStyleWhenStickyAtBottomRight,
}: FullyGuardedPlayerPositionManagerArgs<TPlayerParentContainer>): void => {
  const { setStickyPlayerPosition, setStickyPlayerPhantomHeight } = useMediaStore(
    MediaStoreSelectors.getActions,
  );
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const previousState = useRef<PlayerPositionManagerMachineContext | null>(null);

  const getPlayerAspect = useCallback(() => {
    const aspect = typeof player.aspect === 'function' ? player.aspect() : player.aspect;
    if (typeof aspect !== 'number' || Number.isNaN(aspect)) {
      return DEFAULT_ASPECT_RATIO;
    }
    return aspect;
  }, [player]);

  const playerParentContainerInitialStyles = useRef<Pick<
    CSSStyleDeclaration,
    | 'bottom'
    | 'boxShadow'
    | 'height'
    | 'maxHeight'
    | 'position'
    | 'right'
    | 'top'
    | 'width'
    | 'zIndex'
  > | null>(null);

  const sharedStickyStyles = useMemo(
    () => ({
      position: 'fixed',
      zIndex: '1000',
    }),
    [],
  );

  const getStylesWhenStickyAtBottomRight = useCallback(() => {
    // Override any existing maxHeight, as we have on the anon media page.
    const maxHeightStyle = `calc(65vh - ${DEFAULT_BOTTOM_PX_WHEN_STICKY_AT_BOTTOM_RIGHT}px * 2)`;
    const maxNonNarrowStickyWidthStyle = `calc(${maxHeightStyle} * ${getPlayerAspect()})`;
    const widthStyleFromOptionsOrDefaults =
      widthStyleWhenStickyAtBottomRight ?? `${DEFAULT_WIDTH_PX_WHEN_STICKY_AT_BOTTOM_RIGHT}px`;
    const widthStyle = `min(max(${MINIMUM_WIDTH_PX_WHEN_STICKY_AT_BOTTOM_RIGHT}px, ${widthStyleFromOptionsOrDefaults}), ${maxNonNarrowStickyWidthStyle})`;

    return {
      ...sharedStickyStyles,
      bottom:
        bottomStyleWhenStickyAtBottomRight ?? `${DEFAULT_BOTTOM_PX_WHEN_STICKY_AT_BOTTOM_RIGHT}px`,
      left: '',
      maxHeight: maxHeightStyle,
      right:
        rightStyleWhenStickyAtBottomRight ?? `${DEFAULT_RIGHT_PX_WHEN_STICKY_AT_BOTTOM_RIGHT}px`,
      top: '',
      width: widthStyle,
    };
  }, [
    bottomStyleWhenStickyAtBottomRight,
    getPlayerAspect,
    rightStyleWhenStickyAtBottomRight,
    sharedStickyStyles,
    widthStyleWhenStickyAtBottomRight,
  ]);

  const stylesWhenStickyAtTop = useMemo(() => {
    return {
      ...sharedStickyStyles,
      bottom: '',
      left: leftStyleWhenStickyAtTop ?? '0',
      right: rightStyleWhenStickyAtTop ?? '0',
      top: topStyleWhenStickyAtTop ?? '0',
      width: '',
    };
  }, [
    sharedStickyStyles,
    leftStyleWhenStickyAtTop,
    rightStyleWhenStickyAtTop,
    topStyleWhenStickyAtTop,
  ]);

  const ensureNonStickyStylesSaved = useCallback(() => {
    if (playerParentContainerInitialStyles.current) {
      return playerParentContainerInitialStyles.current;
    }
    const { bottom, boxShadow, height, maxHeight, position, right, top, width, zIndex } =
      playerParentContainer.style;

    playerParentContainerInitialStyles.current = {
      bottom,
      boxShadow,
      height,
      maxHeight,
      position,
      right,
      top,
      width,
      zIndex,
    };

    return playerParentContainerInitialStyles.current;
  }, [playerParentContainer.style]);

  const setPhantomHeight = useCallback(
    (heightPx: number) => {
      // eslint-disable-next-line no-param-reassign
      nonStickyPlayerPhantom.style.height = `${heightPx}px`;
      setStickyPlayerPhantomHeight(heightPx);
    },
    [nonStickyPlayerPhantom.style, setStickyPlayerPhantomHeight],
  );

  const setPlayerParentContainerStylesForStickyAtBottomRight = useCallback(() => {
    Object.assign(playerParentContainer.style, getStylesWhenStickyAtBottomRight());
  }, [getStylesWhenStickyAtBottomRight, playerParentContainer.style]);

  const makePlayerStickyAtBottomRight = useCallback(
    ({ context }: ActionArgs) => {
      ensureNonStickyStylesSaved();

      setPhantomHeight(context.nonStickyPlayerPhantomHeightPx);
      setPlayerParentContainerStylesForStickyAtBottomRight();
      setStickyPlayerPosition('bottom-right');
    },
    [
      ensureNonStickyStylesSaved,
      setPhantomHeight,
      setPlayerParentContainerStylesForStickyAtBottomRight,
      setStickyPlayerPosition,
    ],
  );

  const makePlayerStickyAtTop = useCallback(
    ({ context }: ActionArgs) => {
      ensureNonStickyStylesSaved();

      setPhantomHeight(context.nonStickyPlayerPhantomHeightPx);
      Object.assign(playerParentContainer.style, stylesWhenStickyAtTop);
      setStickyPlayerPosition('top');
    },
    [
      ensureNonStickyStylesSaved,
      playerParentContainer,
      setPhantomHeight,
      setStickyPlayerPosition,
      stylesWhenStickyAtTop,
    ],
  );

  const makePlayerNotSticky = useCallback(
    ({ context }: ActionArgs): void => {
      if (playerParentContainerInitialStyles.current == null) {
        return;
      }

      setPhantomHeight(context.nonStickyPlayerPhantomHeightPx);

      Object.assign(playerParentContainer.style, playerParentContainerInitialStyles.current);
      setStickyPlayerPosition('none');
    },
    [setPhantomHeight, playerParentContainer, setStickyPlayerPosition],
  );

  const playerPositionManagerMachineOptions = useMemo(() => {
    return {
      actions: {
        makePlayerStickyAtBottomRight,
        makePlayerNotSticky,
        makePlayerStickyAtTop,
      },
      context: {
        intersectionObserverTargetHeightPx: playerParentContainer.offsetHeight,
        intersectionObserverRootMarginTopPx: 0,
        nonStickyPlayerPhantomHeightPx: 0,
        playerAspect: null,
      },
      guards: {
        stickyAtTopIsAllowed: ({ context }: ActionArgs) => {
          if (!windowIsNarrow()) {
            return false;
          }

          const wouldBecomeNotStickyImmediately =
            intersectionObserverTarget.getBoundingClientRect().top +
              intersectionObserverTarget.clientHeight +
              context.intersectionObserverRootMarginTopPx >
            0;

          const playerIsHorizontal = getPlayerAspect() > REASONABLY_HORIZONTAL_ASPECT_RATIO;
          const windowIsHorizontal = window.innerWidth > window.innerHeight;
          const playerMightFillScreenIfSticky = playerIsHorizontal && windowIsHorizontal;

          return (
            playerIsHorizontal && !playerMightFillScreenIfSticky && !wouldBecomeNotStickyImmediately
          );
        },
        stickyAtBottomRightIsAllowed: ({ context }: ActionArgs) => {
          const wouldBecomeNotStickyImmediately =
            intersectionObserverTarget.getBoundingClientRect().top +
              intersectionObserverTarget.clientHeight +
              context.intersectionObserverRootMarginTopPx >
            0;
          return !windowIsNarrow() && !wouldBecomeNotStickyImmediately;
        },
      },
    };
  }, [
    getPlayerAspect,
    intersectionObserverTarget,
    makePlayerNotSticky,
    makePlayerStickyAtBottomRight,
    makePlayerStickyAtTop,
    playerParentContainer.offsetHeight,
  ]);

  const [_, send, actor] = useMachine(
    playerPositionManagerMachine.provide(playerPositionManagerMachineOptions),
  );

  const onChangeIntersectionObserverTargetHeightPx = useCallback(
    (heightPx: number) => {
      // eslint-disable-next-line no-param-reassign
      intersectionObserverTarget.style.height = `${heightPx}px`;
    },
    [intersectionObserverTarget.style],
  );

  const onChangeIntersectionObserverRootMarginTopPx = useCallback(
    (marginTopPx: number): void => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }

      intersectionObserverRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            send({ type: 'INTERSECTION_OBSERVER_TARGET_START_INTERSECTING' });
            return;
          }
          send({ type: 'INTERSECTION_OBSERVER_TARGET_STOP_INTERSECTING' });
        },
        {
          root: null,
          rootMargin: `${marginTopPx}px 0px 0px 0px`,
          threshold: 0,
        },
      );

      intersectionObserverRef.current.observe(intersectionObserverTarget);
    },
    [intersectionObserverTarget, send],
  );

  const onChangeNonStickyPlayerPhantomHeightPx = useCallback(
    (heightPx: number): void => {
      setPhantomHeight(heightPx);
    },
    [setPhantomHeight],
  );

  useEffect(() => {
    if (!resizeObserverRef.current) {
      resizeObserverRef.current = new ResizeObserver(() => {
        send({
          type: 'PLAYER_PARENT_CONTAINER_RESIZE',
          height: playerParentContainer.clientHeight,
        });
      });

      resizeObserverRef.current.observe(playerParentContainer);

      return () => {
        resizeObserverRef.current?.disconnect();
      };
    }

    return noOpFn;
  }, [playerParentContainer, send]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      send({
        type: 'PLAYER_PARENT_CONTAINER_RESIZE',
        height: playerParentContainer.clientHeight,
      });
    }, 0);
    return () => {
      clearTimeout(timeout);
    };
  }, [playerParentContainer.clientHeight, send]);

  useEffect(() => {
    actor.subscribe((nextState) => {
      if (isEqual(nextState.context, previousState.current)) {
        return;
      }

      onChangeIntersectionObserverTargetHeightPx(
        nextState.context.intersectionObserverTargetHeightPx,
      );

      onChangeIntersectionObserverRootMarginTopPx(
        nextState.context.intersectionObserverRootMarginTopPx,
      );

      onChangeNonStickyPlayerPhantomHeightPx(nextState.context.nonStickyPlayerPhantomHeightPx);

      previousState.current = nextState.context;
    });
  }, [
    intersectionObserverTarget.style,
    onChangeIntersectionObserverRootMarginTopPx,
    onChangeIntersectionObserverTargetHeightPx,
    onChangeNonStickyPlayerPhantomHeightPx,
    actor,
  ]);

  useEffect(() => {
    const onWindowResize = () => {
      send({ type: 'WINDOW_RESIZE' });
    };

    window.addEventListener('resize', throttle(onWindowResize, HALF_SECOND));

    return () => {
      window.removeEventListener('resize', throttle(onWindowResize, HALF_SECOND));
    };
  }, [send]);
};
